import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { formatAdminAccountMoney } from '../../types/account';
import type { UgotProRefundRequest } from '../../ugotPro/ugotProTypes';
import { UGOT_PRO_REFUND_STATUS_LABELS, formatUgotProDateTime } from '../../ugotPro/ugotProFormat';
import { UgotProRefundEvidence } from './UgotProRefundEvidence';

export type UgotProApproveInput = { amount: string; note: string };

type Props = {
  items: UgotProRefundRequest[];
  loading: boolean;
  error: string | null;
  emailByUserId: ReadonlyMap<string, string>;
  busyKey: string | null;
  onApprove: (refund: UgotProRefundRequest, input: UgotProApproveInput) => void | Promise<void>;
  onReject: (refund: UgotProRefundRequest, reason: string) => void | Promise<void>;
  onOpenUser: (userId: string) => void;
};

function QueueItem({ refund, email, busyKey, onApprove, onReject, onOpenUser }: Omit<Props, 'items' | 'loading' | 'error' | 'emailByUserId'> & {
  refund: UgotProRefundRequest;
  email: string | undefined;
}) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('관리자 검토 후 승인');
  const [rejectReason, setRejectReason] = useState('');
  const busy = busyKey === `approve:${refund.refundRequestId}` || busyKey === `reject:${refund.refundRequestId}`;
  const label = UGOT_PRO_REFUND_STATUS_LABELS[refund.status];
  return (
    <div className="space-y-3 rounded-lg border bg-background p-3" data-testid={`ugot-pro-queue-${refund.refundRequestId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={label.tone}>{label.label}</Badge>
        <span className="font-medium">#{refund.refundRequestId}</span>
        <button type="button" className="text-sm text-primary underline-offset-4 hover:underline" onClick={() => onOpenUser(refund.userId)}>
          {email || refund.userId}
        </button>
        <span className="text-sm text-muted-foreground">{formatUgotProDateTime(refund.createdAt)}</span>
      </div>
      <UgotProRefundEvidence refund={refund} />
      <div className="space-y-3 border-t pt-3">
        <div className="grid gap-2 sm:grid-cols-[160px_minmax(0,1fr)_auto] sm:items-end">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">금액({refund.currency || '-'}, 비우면 전액)</span>
            <Input
              inputMode="decimal"
              placeholder={`전액 ${formatAdminAccountMoney(refund.originalAmount, refund.currency)}`}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">승인 메모</span>
            <Input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </label>
          <Button type="button" disabled={busy || !note.trim()} onClick={() => void onApprove(refund, { amount, note })} data-testid={`ugot-pro-approve-${refund.refundRequestId}`}>
            {busyKey === `approve:${refund.refundRequestId}` && <Loader2 className="h-4 w-4 animate-spin" />}
            {amount.trim() ? '부분 환불 승인' : '전액 환불 승인'}
          </Button>
        </div>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">거절 사유(고객에게 남는 기록)</span>
            <Input value={rejectReason} maxLength={500} onChange={(event) => setRejectReason(event.target.value)} />
          </label>
          <Button type="button" variant="outline" disabled={busy || !rejectReason.trim()} onClick={() => void onReject(refund, rejectReason.trim())} data-testid={`ugot-pro-reject-${refund.refundRequestId}`}>
            {busyKey === `reject:${refund.refundRequestId}` && <Loader2 className="h-4 w-4 animate-spin" />}
            거절
          </Button>
        </div>
      </div>
    </div>
  );
}

/** 환불 검토 대기열(PENDING). 자동 승인 규칙에 걸리지 않은 요청이 여기 모인다. */
export function UgotProRefundQueue({ items, loading, error, emailByUserId, ...actions }: Props) {
  return (
    <Card data-testid="ugot-pro-refund-queue">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          환불 검토 대기 <Badge variant={items.length ? 'warning' : 'outline'}>{items.length}건</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-muted-foreground">불러오는 중…</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}
        {!loading && !error && items.length === 0 && <p className="text-sm text-muted-foreground">검토할 환불 요청이 없습니다.</p>}
        {items.map((refund) => (
          <QueueItem key={refund.refundRequestId} refund={refund} email={emailByUserId.get(refund.userId)} {...actions} />
        ))}
        <p className="text-xs text-muted-foreground">
          승인하면 Paddle 에 환불을 요청합니다. Paddle 이 바로 받아들이지 않으면 "Paddle 승인 대기"로 남고, 주문 REFUNDED 는 웹훅이 확정합니다.
        </p>
      </CardContent>
    </Card>
  );
}
